import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Cable, CheckCircle2, ArrowRight, ChevronRight,
  RefreshCw, Cpu, BadgeCheck, ArrowUpRight, Layers, Car, Gauge,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, PortalButton } from "@/components/Motion";
import { VehicleCompatibility } from "@/components/VehicleCompatibility";
import { API, PORTAL_URL, SITE_ORIGIN, money, setJsonLd, removeJsonLd } from "@/lib/site";

// The three existing KESS3 Slave — Regal Tuning packages, pulled live from the database.
const KESS3_REGAL = ["kess3-slave-regal-cars-obd", "kess3-slave-regal-cars-obd-bench", "kess3-slave-regal-full"];

const FEATURES = [
  [Cable, "OBD, Bench & Boot in One Tool", "One compact interface covering OBD programming through the diagnostic port, Bench work on the ECU or TCU, and Boot mode access — no separate tools needed."],
  [Cpu, "ECU & TCU Coverage", "Reads and writes engine and transmission control units across a continuously expanding protocol database."],
  [Car, "Wide Vehicle Coverage", "Cars and LCVs, bikes and ATVs, agriculture, truck and bus, and marine applications — depending on the activations you select."],
  [Gauge, "Fast, Stable Communication", "A newer, faster hardware generation built for daily workshop use — quick reads, stable writes and automatic checksum handling where supported."],
  [Layers, "Modular Activations", "Pay for the protocols you actually need — hardware plus your choice of vehicle-group activations, expandable later as your work grows."],
  [RefreshCw, "KESSv2 & K-TAG Successor", "KESS3 replaces both KESSv2 and K-TAG with a single device, one software platform and ongoing Alientech development."],
];

const METHODS = [
  ["OBD", "Programming through the vehicle's diagnostic socket — the fastest method for supported vehicles, with no need to remove the control unit."],
  ["Bench", "Direct connection to the ECU or TCU on the bench without opening the unit — deeper access than OBD for supported protocols."],
  ["Boot", "Low-level access with the control unit open, used on supported units for programming, backup, recovery and cloning-style operations."],
];

const WHY_REGAL = [
  "Professional ECU tuning specialists — not a box-shifting reseller",
  "Genuine Alientech equipment, supplied configured and ready to work",
  "KESS3 Slave packages linked directly to Regal Tuning's file service",
  "Master and Slave routes with modular activations to suit your budget",
  "In-house ECU tuning files, developed and verified on our 4WD dyno",
  "Technical support from tuners with 20+ years of industry experience",
];

const FAQS = [
  ["What is Alientech KESS3?", "KESS3 is Alientech's latest-generation ECU and TCU programming tool. It combines the heritage of KESSv2 (OBD programming) and K-TAG (bench and boot programming) into a single device with one software platform, covering cars, LCVs, bikes, agriculture, truck, bus and marine applications depending on the activations you choose."],
  ["What is the difference between KESS3 Master and KESS3 Slave?", "The hardware is the same — the difference is the licence. A KESS3 Master reads and writes open files you can edit yourself or send to any file provider, and can be linked to its own network of Slave tools. A KESS3 Slave works with encrypted files that are processed by the Master tool owner it is linked to — your file provider."],
  ["Should I buy KESS3 Master or KESS3 Slave?", "Choose Master if you want full control of your tuning files or plan to supply other tuners. Choose Slave if you want a lower-cost route in, working with a trusted Master file provider. Our KESS3 Slave packages linked to Regal Tuning start from £1,100 all-in, and you can compare both routes with our Build Your KESS3 configurators."],
  ["Can KESS3 program both ECUs and TCUs?", "Yes — KESS3 covers engine control units and transmission control units, subject to protocol support for the specific unit and the activations on your licence."],
  ["Does KESS3 support OBD, Bench and Boot?", "Yes, all three connection methods are built into the same device. Which method applies depends on the specific ECU or TCU and protocol — not every unit supports every method, so coverage should be checked for the vehicles you work on."],
  ["What vehicles does KESS3 cover?", "KESS3 activations are grouped by vehicle type: Car/LCV, Bike/ATV/UTV, Agriculture/Truck/Bus and Marine/PWC, each available as OBD or Bench + Boot packages. Exact coverage depends on the protocol for the specific ECU or TCU — contact us with your typical vehicles and we'll confirm before you buy."],
  ["What happens if I buy a KESS3 Slave linked to Regal Tuning?", "Your KESS3 Slave is supplied linked to Regal Tuning as your Master. You read the vehicle with your tool, submit the file to us, and receive a professionally developed calibration back through our tuning file service — backed by our technical support team."],
  ["Can I get a KESS3 Slave for a different Master?", "Yes — if you already have a chosen Master provider, use our Build Your KESS3 Slave configurator to put together the hardware plus the activations you need, ready to link to your Master."],
  ["Can I add more activations to my KESS3 later?", "Yes. KESS3 is modular — you can start with a single vehicle-group activation and add further protocol packages later as your workload grows. Contact us for upgrade pricing on the activations you need."],
  ["Do I need an active subscription?", "It depends on the package. KESS3 update subscriptions are available in our range — what is included varies by package and can change over time, so check the specific product or ask us before ordering."],
  ["How much does a KESS3 cost?", "KESS3 is priced as hardware plus activations: the hardware base is £500 and each activation package is priced separately, so you only pay for the coverage you need. Our ready-made KESS3 Slave packages linked to Regal Tuning start from £1,100 all-in, and the configurators show live totals as you select activations."],
  ["Is KESS3 better than KESSv2 or K-TAG?", "KESS3 is the successor to both — one device instead of two, with faster hardware, a single software platform and ongoing Alientech development. New protocols are developed for KESS3, making it the sensible choice for new professional setups."],
];

export default function Kess3() {
  const [products, setProducts] = useState([]);
  const [hwPrice, setHwPrice] = useState(500);

  useEffect(() => {
    // canonical is managed globally by ScrollManager in App.js
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
        { "@type": "ListItem", position: 2, name: "Tuning Tools", item: SITE_ORIGIN + "/tuning-tools" },
        { "@type": "ListItem", position: 3, name: "Alientech KESS3", item: SITE_ORIGIN + "/alientech-kess3" },
      ],
    });
    setJsonLd("ld-faq", {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    return () => { removeJsonLd("ld-breadcrumb"); removeJsonLd("ld-faq"); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/products?category=alientech&limit=50`)
      .then((r) => {
        const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
        setProducts(KESS3_REGAL.map((s) => bySlug[s]).filter(Boolean));
      })
      .catch(() => {});
    axios.get(`${API}/kess3-config`).then((r) => setHwPrice(r.data.hardware_price ?? 500)).catch(() => {});
  }, []);

  return (
    <main data-testid="kess3-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24" data-testid="kess3-hero">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="container-regal relative">
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb" data-testid="kess3-breadcrumbs">
            <Link to="/" className="hover:text-regal-amber">Home</Link>
            <ChevronRight size={12} />
            <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
            <ChevronRight size={12} />
            <span className="text-neutral-300">Alientech KESS3</span>
          </nav>
          <Reveal><p className="overline-label mb-4">Genuine Alientech Equipment</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight text-ink">
              Alientech KESS3 ECU &amp; TCU Tuning Tool
            </h1>
          </Reveal>
          <Reveal delay={0.16} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed">
              KESS3 is Alientech's latest professional ECU and TCU programming tool — OBD, Bench and Boot in a single device, with modular activations so you only pay for the vehicle coverage you need. Available as a Master tool or as a Slave linked to your chosen file provider.
            </p>
          </Reveal>
          <Reveal delay={0.24} className="mt-9 flex flex-wrap gap-4">
            <a href="#kess3-packages" data-testid="kess3-shop-cta" className="inline-flex items-center gap-2 bg-regal-amber px-9 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
              Shop KESS3 Packages <ArrowRight size={15} />
            </a>
            <a href="#kess3-options" data-testid="kess3-options-cta" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              View KESS3 Options
            </a>
          </Reveal>
        </div>
      </section>

      {/* WHAT IS KESS3 */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="kess3-what">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">What is Alientech KESS3?</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Three programming methods, one device</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
              KESS3 reads and writes engine and transmission control units through OBD, Bench and Boot connections in a single tool. Exact functions and vehicle coverage depend on the specific ECU or TCU, the protocol, and the activations you select.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="h-full border border-line bg-white p-7 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
                  <Icon size={22} className="text-regal-amber" strokeWidth={1.5} />
                  <h3 className="mt-4 font-display text-base font-bold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 flex items-start gap-3 border border-line bg-white p-5 text-sm text-neutral-400" data-testid="kess3-coverage-note">
              <Car size={16} className="mt-0.5 shrink-0 text-regal-amber" />
              Coverage note: not every vehicle supports every programming mode. Tell us the vehicles you work on and we'll confirm KESS3 coverage before you buy.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MASTER VS SLAVE */}
      <section id="kess3-options" className="border-t border-line py-20 sm:py-24 scroll-mt-24" data-testid="kess3-compare">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">KESS3 Master vs KESS3 Slave</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-12 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Same hardware, two ways to work</h2></Reveal>
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-master-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Independence</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">KESS3 Master</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  KESS3 Master is intended for professional tuners who want control over their tuning files. Master tools read and write open files — edit them yourself with compatible calibration software, or send them to any file provider you choose.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  A Master tool can also be linked to its own network of KESS3 Slave users, letting you supply other workshops and grow a dealer network under your own brand.
                </p>
                <div className="mt-auto pt-8">
                  <Link to="/build-your-kess3/master" data-testid="view-kess3-master" className="inline-flex items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
                    Build Your KESS3 Master <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-slave-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Supported</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">KESS3 Slave</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  KESS3 Slave is designed for workshops and tuners who work with a chosen Master file provider. Slave files are encrypted, and file modification is handled by the Master your tool is linked to — you read and write the vehicle, your Master supplies the calibration.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  Choose a KESS3 Slave linked to Regal Tuning and you combine the hardware with our professional <Link to="/tuning-files" className="text-regal-amber underline-offset-4 hover:underline">ECU tuning file service</Link> and technical support — developed in-house and verified on our 4WD dyno.
                </p>
                <div className="mt-auto pt-8">
                  <a href="#kess3-packages" data-testid="view-regal-kess3-slave" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                    View Regal KESS3 Slave Packages <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBD / BENCH / BOOT */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="kess3-methods">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Programming Methods</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-5 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">OBD, Bench &amp; Boot explained</h2></Reveal>
          <Reveal delay={0.1}><p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">Three ways to reach the control unit — all in one device. Which method applies depends on the specific ECU or TCU and protocol.</p></Reveal>
          <div className="grid gap-px border border-line bg-line sm:grid-cols-3">
            {METHODS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07} className="bg-white">
                <div className="h-full p-8">
                  <p className="font-mono text-xs tracking-[0.3em] text-regal-amber">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KESS3 VEHICLE COMPATIBILITY */}
      <section id="kess3-compatibility" className="border-t border-line bg-surface py-20 sm:py-24 scroll-mt-24" data-testid="kess3-compat-section">
        <div className="container-regal">
          <Reveal>
            <VehicleCompatibility
              apiBase="kess3-vehicles"
              testId="kess3-compat"
              ctaTo="#kess3-packages"
              ctaLabel="View KESS3 Packages"
              heading="KESS3 Vehicle Compatibility"
              intro="Check whether your vehicle, ECU or TCU is covered by KESS3 — search by make, model, engine, ECU and see the available connection methods (OBD, Bench, Boot). Compatibility data comes from the official Alientech vehicle database; exact coverage depends on the control unit and activations."
            />
          </Reveal>
        </div>
      </section>

      {/* CONFIGURATORS */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="kess3-builders">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Configure Your KESS3</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Hardware {money(hwPrice, "GBP")} + your choice of activations</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
              Build the exact KESS3 setup you need: pick Master or Slave, then select activations by vehicle group and connection method. Live totals as you configure.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              ["master", "Build Your KESS3 – Master", "KESS3 hardware plus your choice of Master activations — full control of your tuning files."],
              ["slave", "Build Your KESS3 – Slave", "KESS3 hardware plus Slave activations — ready to link to Regal Tuning or your own chosen Master."],
            ].map(([t, title, d], i) => (
              <Reveal key={t} delay={i * 0.07}>
                <Link to={`/build-your-kess3/${t}`} data-testid={`kess3-build-${t}-card`} className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber/40 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)] transition-all duration-300">
                  <div className="overflow-hidden border-b border-line">
                    <img src="/assets/brands/alientech.webp" alt={`${title} — Alientech KESS3 ECU and TCU tuning tool`} loading="lazy" className="aspect-[16/8] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">{title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">{d}</p>
                    <div className="mt-auto flex items-center justify-between pt-6">
                      <p className="font-mono text-xs uppercase tracking-[0.14em] text-regal-amber">Hardware {money(hwPrice, "GBP")} + activations</p>
                      <span className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-ink group-hover:text-regal-amber transition-colors">Configure <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REGAL KESS3 SLAVE PACKAGES */}
      <section id="kess3-packages" className="border-t border-line bg-surface py-20 sm:py-24 scroll-mt-24" data-testid="kess3-products">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Best Value — Linked to Regal Tuning</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">KESS3 Slave — Regal Tuning packages</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
              Ready-made KESS3 Slave packages linked to Regal Tuning as your Master — hardware and activation included, all-in pricing, backed by our tuning file service.
            </p>
          </Reveal>
          {products.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="kess3-product-grid">
              {products.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.05}>
                  <Link to={`/product/${p.slug}`} data-testid={`kess3-product-${p.slug}`} className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber transition-colors">
                    <div className="border-b border-line bg-white p-8">
                      <img src={p.images?.[0] || "/assets/brands/alientech.webp"} alt={`${p.name} — Alientech KESS3 Slave tuning tool`} loading="lazy" className="mx-auto h-28 object-contain" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-display text-sm font-bold uppercase tracking-wide text-ink group-hover:text-regal-amber transition-colors">{p.name}</h3>
                      <p className="mt-auto pt-4 font-display text-lg font-extrabold text-ink">{money(p.sale_price || p.price_ex_vat, p.currency)}</p>
                      <span className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-regal-amber">
                        View product <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="border border-dashed border-linebright p-8 text-sm text-neutral-400" data-testid="kess3-products-pending">
              KESS3 packages are listed in our <Link to="/tuning-tools/alientech" className="text-regal-amber underline-offset-4 hover:underline">Alientech shop section</Link>.
            </p>
          )}
        </div>
      </section>

      {/* WHY REGAL */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="kess3-why-regal">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">Why Buy KESS3 From Regal Tuning?</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Hardware plus real tuning support</h2></Reveal>
          <div className="grid gap-3 sm:grid-cols-2">
            {WHY_REGAL.map((a, i) => (
              <Reveal key={a} delay={i * 0.04}>
                <p className="flex items-center gap-3 border border-line bg-surface p-4 text-sm font-medium text-ink">
                  <BadgeCheck size={16} className="shrink-0 text-regal-amber" /> {a}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm leading-relaxed text-neutral-400">
              New to professional tuning? Our <Link to="/become-a-dealer" className="text-regal-amber underline-offset-4 hover:underline">dealer programme</Link> combines equipment, tuning files and ongoing technical support to help workshops add ECU tuning properly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FILE SERVICE */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="kess3-file-support">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">Regal Tuning File Service</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mb-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Pair KESS3 with professional tuning files</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm sm:text-base text-neutral-400 leading-relaxed">
              Regal Tuning operates a professional <Link to="/tuning-files" className="text-regal-amber underline-offset-4 hover:underline">ECU tuning file service</Link> for tuning businesses and workshops worldwide. KESS3 Slave customers linked to Regal Tuning submit files straight to our calibration team — and KESS3 Master users keep full independence, using us as their file provider by choice.
            </p>
          </Reveal>
          <Reveal delay={0.16} className="mt-8 flex flex-wrap items-center gap-4">
            <PortalButton label="Upload a File" testId="kess3-upload-cta" />
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-regal-amber underline-offset-4 hover:underline" data-testid="kess3-portal-link">
              Existing customer? File Portal <ArrowUpRight className="inline" size={13} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="kess3-faq">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">FAQ</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Alientech KESS3 questions, answered</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="kess3-faq-accordion">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-line" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-sm sm:text-base font-semibold text-ink hover:text-regal-amber hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-neutral-400">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 text-sm text-neutral-400">
              Ready to order? Browse the <Link to="/tuning-tools/alientech" className="text-regal-amber underline-offset-4 hover:underline">Alientech range</Link>, configure a <Link to="/build-your-kess3/master" className="text-regal-amber underline-offset-4 hover:underline">KESS3 Master</Link>, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">contact our team</Link> for advice on the right setup.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
